import { Box, Button, Fade, useToast } from '@chakra-ui/react'
import { FileGenerator } from 'components/molecules/FileGenerator'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { useGenerateFiles } from 'hooks/useGenerateFIles'
import { DocumentTypes, TModes } from 'pages/Report/types'
import { useState } from 'react'
import {
    generateDashboardManagementReportPDF,
    getFilesManagementReports,
    getPendingImagesReport
} from 'services/report'
import { generateBlobFile, generateFile } from 'utils/blob'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { FilterManagementReport } from '../Filter/interfaces'
import { ModeDashboard } from './ModeDashboard'
import { ModeTable } from './ModeTable'
import { initialStateFilter } from './constanst'

export const initialFiltersManagementReport = initialStateFilter

export const ManagementReport: React.FC = () => {
    const [currentMode, setCurrentMode] = useState<TModes>('table')
    const [loadingFile, setLoadingFile] = useState<boolean>(false)
    const [filters, setFilters] = useState<FilterManagementReport>(
        initialFiltersManagementReport
    )
    const { getImageBase64Dashboards } = useGenerateFiles()

    const toast = useToast()
    const { isBackoffice } = useAuthContext()
    const { currentAgent, currentInstitution } = useInstitutionsContext()

    const modeTable = currentMode === 'table'

    const labelMode = modeTable ? 'Modo Dashboard' : 'Modo Tabela'

    const onChangeMode = () => {
        const newMode = modeTable ? 'dashboard' : 'table'
        setCurrentMode(newMode)
        setFilters(initialFiltersManagementReport)
    }

    const fetchGenerateFile = (type: DocumentTypes) => {
        downloadFiles(type, filters)
    }

    const wrapperIdDashboard = 'managment-report'

    const buildPayloadGenerateFile = (
        type: DocumentTypes,
        filter: FilterManagementReport
    ) => {
        const filters = removeEmptyValues({ ...filter })

        return {
            tipo: type,
            cnpjAgente: currentAgent?.cnpj ?? '',
            ...filters,
            periodoFinal: filter?.periodoFinal
                ? formatter.formatDateToPayload(filter.periodoFinal)
                : '',
            periodoInicial: filter?.periodoInicial
                ? formatter.formatDateToPayload(filter.periodoInicial)
                : ''
        }
    }

    const downloadFiles = async (
        type: DocumentTypes,
        filter: FilterManagementReport
    ) => {
        try {
            setLoadingFile(true)

            if (!modeTable) {
                const imageBase64 = await getImageBase64Dashboards(
                    'managment-report'
                )

                if (imageBase64) {
                    const response = await generateDashboardManagementReportPDF(
                        buildPayloadGenerateFile(type, filter),
                        imageBase64,
                        currentInstitution?.id,
                        isBackoffice
                    )

                    generateFile('PDF', response)

                    toast({
                        title: 'Arquivo gerado com sucesso!',
                        description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                        position: 'bottom-left',
                        status: 'success',
                        isClosable: true
                    })

                    return
                }
            }

            const response = await getFilesManagementReports(
                buildPayloadGenerateFile(type, filter),
                currentInstitution?.id,
                isBackoffice
            )

            generateFile(type, response)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    const downloadPendingImages = async () => {
        try {
            setLoadingFile(true)

            const response = await getPendingImagesReport(
                currentInstitution?.id,
                currentAgent?.cnpj,
                isBackoffice
            )
            generateBlobFile(response, 'registros_sem_imagem.csv')

            toast({
                title: 'Arquivo gerado com sucesso!',
                description:
                    'O relatório de imagens pendentes foi gerado. Verifique sua pasta de downloads para acessá-lo.',
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description:
                    'Não foi possível gerar o relatório de imagens pendentes. Tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    return (
        <>
            <Box display="flex" justifyContent="flex-end" marginBottom="3">
                <Button
                    marginRight="8px"
                    colorScheme="primary"
                    type="button"
                    onClick={onChangeMode}
                >
                    {labelMode}
                </Button>

                <FileGenerator
                    callAPI={fetchGenerateFile}
                    onPendingImages={
                        modeTable ? downloadPendingImages : undefined
                    }
                    onlyPDF={!modeTable}
                    loading={loadingFile}
                />
            </Box>

            <Fade in={modeTable}>
                {modeTable && <ModeTable onFilter={setFilters} />}
            </Fade>
            <Fade in={!modeTable}>
                {!modeTable && (
                    <ModeDashboard
                        onFilter={setFilters}
                        idDashboard={wrapperIdDashboard}
                    />
                )}
            </Fade>
        </>
    )
}
